import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PackageCheck, ShoppingCart } from "lucide-react";
import { useState } from "react";

import { Button, Card, EmptyState, Pill, SkeletonRows } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney, formatRelativeTime } from "@/lib/format";
import {
  procurementRepository,
  type PurchaseOrder,
  type PurchaseOrderStatus,
} from "@/repositories/procurementRepository";
import { useAuthStore } from "@/stores/authStore";

import { ReceiveDialog } from "./ReceiveDialog";

const STATUS_TONE: Record<PurchaseOrderStatus, "neutral" | "info" | "warn" | "success" | "danger"> =
  {
    DRAFT: "neutral",
    APPROVED: "info",
    PARTLY_RECEIVED: "warn",
    RECEIVED: "success",
    CANCELLED: "danger",
  };

const STATUS_LABEL: Record<PurchaseOrderStatus, string> = {
  DRAFT: "Draft",
  APPROVED: "Approved",
  PARTLY_RECEIVED: "Part delivered",
  RECEIVED: "Delivered",
  CANCELLED: "Cancelled",
};

/**
 * Ordering stock, and receiving it.
 *
 * Two lists rather than one, because they answer different questions. Orders
 * answer "what have we asked for"; receipts answer "what actually turned up".
 * Merging them into a single feed would bury the gap between the two, and that
 * gap — short deliveries, substitutions, quiet price rises — is the thing worth
 * looking at.
 *
 * Receiving is reachable straight from an approved order, because that is where
 * somebody is standing when the van arrives.
 */
export function ProcurementPage() {
  const [tab, setTab] = useState<"orders" | "receipts">("orders");
  const [receiving, setReceiving] = useState<PurchaseOrder | null>(null);

  const canApprove = useAuthStore((s) => s.can("procurement.approve_order"));
  const canReceive = useAuthStore((s) => s.can("procurement.receive_goods"));
  const queryClient = useQueryClient();

  const orders = useQuery({
    queryKey: ["procurement", "orders"],
    queryFn: () => procurementRepository.orders(),
    enabled: tab === "orders",
  });

  const receipts = useQuery({
    queryKey: ["procurement", "receipts"],
    queryFn: () => procurementRepository.receipts(),
    enabled: tab === "receipts",
  });

  const approve = useMutation({
    mutationFn: (id: string) => procurementRepository.approve(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["procurement"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {(["orders", "receipts"] as const).map((value) => (
          <Button
            key={value}
            variant={tab === value ? "primary" : "ghost"}
            onClick={() => setTab(value)}
          >
            {value === "orders" ? "Purchase orders" : "Deliveries"}
          </Button>
        ))}
      </div>

      <Card>
        {tab === "orders" ? (
          orders.isLoading ? (
            <SkeletonRows rows={6} />
          ) : (orders.data ?? []).length === 0 ? (
            <EmptyState
              icon={ShoppingCart}
              title="No purchase orders"
              description="Raise one to record what has been ordered, so a delivery can be checked against it."
            />
          ) : (
            <ul className="divide-y divide-line">
              {(orders.data ?? []).map((order) => (
                <li key={order.id} className="flex flex-wrap items-center gap-3 py-3">
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium tabular-nums text-ink-900">
                        {order.reference}
                      </span>
                      <Pill tone={STATUS_TONE[order.status]}>{STATUS_LABEL[order.status]}</Pill>
                    </span>
                    <span className="block text-xs text-ink-500">
                      {order.supplier_name} · {order.line_count}{" "}
                      {order.line_count === 1 ? "line" : "lines"}
                      {order.expected_at && ` · due ${formatDate(order.expected_at)}`}
                    </span>
                  </span>

                  <span className="text-sm tabular-nums text-ink-900">
                    {formatMoney(order.total_cost)}
                  </span>

                  {order.status === "DRAFT" && canApprove && (
                    <Button
                      size="sm"
                      loading={approve.isPending && approve.variables === order.id}
                      onClick={() => approve.mutate(order.id)}
                    >
                      Approve
                    </Button>
                  )}

                  {canReceive &&
                    (order.status === "APPROVED" || order.status === "PARTLY_RECEIVED") && (
                      <Button variant="primary" size="sm" onClick={() => setReceiving(order)}>
                        <PackageCheck size={14} aria-hidden /> Receive
                      </Button>
                    )}
                </li>
              ))}
            </ul>
          )
        ) : receipts.isLoading ? (
          <SkeletonRows rows={6} />
        ) : (receipts.data ?? []).length === 0 ? (
          <EmptyState
            icon={PackageCheck}
            title="No deliveries recorded"
            description="Every delivery recorded here goes straight into stock."
          />
        ) : (
          <ul className="divide-y divide-line">
            {(receipts.data ?? []).map((receipt) => (
              <li key={receipt.id} className="flex flex-wrap items-center gap-3 py-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium tabular-nums text-ink-900">
                    {receipt.reference}
                    {receipt.order_reference && (
                      <span className="ml-2 font-normal text-ink-500">
                        against {receipt.order_reference}
                      </span>
                    )}
                  </span>
                  <span className="block text-xs text-ink-500">
                    {receipt.supplier_name} · {formatRelativeTime(receipt.received_at)}
                    {receipt.received_by_name && ` · ${receipt.received_by_name}`}
                  </span>
                </span>

                <span className={cn("text-sm tabular-nums text-ink-900")}>
                  {formatMoney(receipt.total_cost)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {receiving && (
        <ReceiveDialog
          order={receiving}
          onClose={() => setReceiving(null)}
          onReceived={() => {
            setReceiving(null);
            void queryClient.invalidateQueries({ queryKey: ["procurement"] });
            // The delivery has just moved stock, so the inventory screen's
            // figures are stale the moment this returns.
            void queryClient.invalidateQueries({ queryKey: ["inventory"] });
          }}
        />
      )}
    </div>
  );
}
